import { Component, Element, h, Host, Prop, State } from "@stencil/core";
import { AppStateType, AppStoreType, getDateFromTimestamp, getRandomId, getSize } from "../../utils/utils";
import { Unsubscribe } from "../../utils/types";


@Component({
  tag: "covet-pics-highlighted",
  styleUrl: "covet-pics-highlighted.scss"
})

export class CovetPicsHighlighted {
  @Element() el: HTMLCovetPicsHighlightedElement;

  /**
   * Number of pages
   */
  @Prop() numberOfPages: number = 0;

  /**
   * array of items to be showed in grid
   */
  @Prop() items: Array<any> = [];

  /**
   * App store
   */
  @Prop() appStore: AppStoreType;

  // App state;
  private appState: AppStateType;
  private unsubscribe: Unsubscribe;
  private layout: string; // short var name (slider, zigzag, spotlight, row)

  @State() productLinks: Array<any> = [];

  @State() mainSliderIndex: number = 0;

  @State() mainSliderAnimatingSlides: Array<any> = [];

  @State() windowHeight: number;

  @State() isDesktopScreen: boolean;

  private pageSliderId: string;

  componentWillLoad() {
    this.initStore();
    this.pageSliderId = `covet-pics-highlighted-page-slider-${getRandomId(16)}`;
  }
  
  componentDidLoad() {
    if (this.layout === "slider" || this.layout === "spotlight") this.initMainSlider();
  }
  
  componentDidRender() {
    document.onkeydown = (e) => this.onSliderClickHandler(e);
  }

  disconnectedCallback() {
    this.unsubscribe();
  }
  
  private initStore() {
    this.appState = this.appStore.getState();
    this.layout = this.appState.settings.highlight_layout;
    this.isDesktopScreen = this.appState.size.isDesktopScreen;
    this.windowHeight = this.appState.size.windowHeight;
    this.unsubscribe = this.appStore.subscribe(() => {
      this.appState = this.appStore.getState();
      this.layout = this.appState.settings.highlight_layout;
      this.isDesktopScreen = this.appState.size.isDesktopScreen;
      this.windowHeight = this.appState.size.windowHeight;
    });
  }

  private isClickOutsideHotspot(e) {
    const activeHotspots: Array<HTMLElement> = Array.from(this.el.querySelectorAll(".hotspot.active.show-content"));
    if (activeHotspots.length > 1)
      return activeHotspots.forEach(hotspot => hotspot.classList.remove("active", "show-content"));
    if (activeHotspots.length === 1 && !activeHotspots[0].contains(e.target))
      return activeHotspots[0].classList.remove("active", "show-content");

    return true;
  }

  private nextTick = (fn) => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(fn);
    });
  };

  private setCSSVariables() {
    return {
      "--covet-pics-highlighted-hotspot-bg-color": this.appState.settings.hotspots.background_color.toString(),
      "--covet-pics-highlighted-background-color": this.appState.settings.background_color.toString(),
      "--covet-pics-highlighted-padding": this.appState.settings.padding.toString() + "%",
      "--covet-pics-highlighted-padding-px": getSize(this.el, "width") * this.appState.settings.padding / 100 + "px", // TODO check which one we real use padding or padding-px
      "--covet-pics-highlighted-primary-color": this.appState.settings.primary_color.toString(),
      "--covet-pics-highlighted-secondary-color": this.appState.settings.secondary_color.toString(),
      "--covet-pics-highlighted-window-height": `${this.windowHeight * 0.01}px`
    };
  }

  private renderControlIcon(direction: string) {
    return direction === "next"
      ? <svg class="icon-arrow" viewBox="0 0 13 8" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M3.89 7.778V6.364L1.98 4.455h10.957v-1H1.848l2.041-2.04V0L0 3.89l3.89 3.888z" fill-rule="nonzero" /></svg>
      : <svg class="icon-arrow rotate" viewBox="0 0 13 8" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M3.89 7.778V6.364L1.98 4.455h10.957v-1H1.848l2.041-2.04V0L0 3.89l3.89 3.888z" fill-rule="nonzero" /></svg>;
  }

  private onSliderClickHandler(e) {
    if (e.keyCode === 39) this.mainSliderChangeSlide(e, "next");
    if (e.keyCode === 37) this.mainSliderChangeSlide(e, "prev");

    return true;
  }

  private initMainSlider() {
    const slides = Array.from(this.el.querySelectorAll(".main-slider .page"));
    if (slides.length === 0) return false;
    const active = slides[this.mainSliderIndex];

    const showIntro = () => {
      const enterHandler = () => {
        active.classList.remove("slide-noactive");
        active.classList.add("slide-active");
        active.classList.remove("before-enter");
        active.classList.remove("enter");
        active.removeEventListener("transitionend", enterHandler);
      };

      active.classList.add("before-enter");
      this.nextTick(() => {
        active.classList.add("enter");
        active.addEventListener("transitionend", enterHandler);
      });
    };

    this.mainSliderAnimationLoop();
    return showIntro();
  }

  private thumbnailAltTag(itemType: string, altTag: string, caption: string, idx: number, itemsLength: number, createdTime: string) {
    if (itemType === "video") return altTag; // for video always show altTag even if null (video the title will be hidden)
    
    if (altTag && altTag !== null) return altTag;
    
    return caption && caption.length > 0 
      ? `${caption}, page ${idx + 1}/${itemsLength}, post created ${getDateFromTimestamp(createdTime)}`
      : `page ${idx + 1}/${itemsLength}, post created ${getDateFromTimestamp(createdTime)}`;
  }

  private pageAriaLabel(item, index: number, itemsLength: number, layout: string, isMobile: boolean): string {
    const isItemReverse = index % 2 !== 0 && !isMobile && layout === "zigzag";

    const thumbnailSide = () => {
      if (isMobile) return "top";
      if (this.layout === "zigzag") return !isItemReverse ? "left" : "right";
      if (this.layout === "spotlight") return "top";
      return "left";
    };
    
    const productSide = () => {
      if (isMobile) return "bottom";
      if (this.layout === "zigzag") return !isItemReverse ? "right" : "left";
      if (this.layout === "spotlight") return "bottom";
      return "right";
    };
    
    const sideLabel = item.links && item.links.length > 0
      ? `on ${thumbnailSide()} and product slider on ${productSide()} side of page`
      : "";

    if (this.layout === "zigzag" && item.video) 
      return `page ${index + 1} of ${itemsLength} pages, with video post ${sideLabel} in vertical zigzag layout`;
    if (this.layout === "zigzag" && item.data_type) 
      return `page ${index + 1} of ${itemsLength} pages, with thumbnail slider ${sideLabel} in vertical zigzag layout`;
    if (this.layout === "zigzag") 
      return `page ${index + 1} of ${itemsLength} pages, with thumbnail img preview ${sideLabel} in vertical zigzag layout`;

    const rowLabel = isMobile
      ? "in vertical layout"
      : `in row ${Math.round(index / 3) + 1} of ${Math.round(itemsLength / 3)} in grid layout`;

    if (this.layout === "row" && item.video) 
      return `thumbnail ${index + 1} of ${itemsLength} with video post ${rowLabel}`;
    if (this.layout === "row" && item.data_type === "carousel") 
      return `thumbnail ${index + 1} of ${itemsLength} with slider posts ${rowLabel}`;
    if (this.layout === "row")
      return `thumbnail ${index + 1} of ${itemsLength} with image post ${rowLabel}`;

    // for slider and spotlight
    if (item.video) 
      return `page ${index + 1} of ${itemsLength} pages, with video post ${sideLabel} in page slider layout`;
    if (item.data_type === "carousel") 
      return `page ${index + 1} of ${itemsLength} pages, with thumbnail slider ${sideLabel} in page slider layout`;

    return `page ${index + 1} of ${itemsLength} pages, with thumbnail img preview ${sideLabel} in page slider layout`;
  }

  private renderSlider() {
    return (
      <section id={this.pageSliderId} class={`main-slider ${this.layout}`} role="region" aria-label="Page slider" aria-labelledby="Page slider">
        <button disabled={this.mainSliderIndex === 0}
          onClick={(e) => this.mainSliderChangeSlide(e, "prev")}
          class={`slider-control main next ${this.layout === "spotlight" ? "main-spotlight" : ""}`}
          aria-label="Previous page"
          aria-controls={this.pageSliderId}
          type="button">
          {this.renderControlIcon("next")}
        </button>
        {this.items.map((item, index) =>
          <covet-pics-highlighted-page class={`${this.layout} slide-noactive`}
            thumbnailUrl={item.data_type === "image" ? item.images.high_resolution : null} // TODO need add carousel, (temp ver)
            thumbnailAlt={this.thumbnailAltTag(item.data_type, item.alt_tag, item.caption, index, this.items.length, item.created_time)}
            ariaLabel={this.pageAriaLabel(item, index, this.items.length, this.layout, !this.isDesktopScreen)}
            thumbnailLayout={item.data_type}
            thumbnailCarouselImages={item.data_type === "carousel" ? item.carousel_media : null}
            videoUrl={item.video ? item.video : null}
            videoPoster={item.cover_images && item.cover_images.high_resolution ? item.cover_images.high_resolution : null}
            productLinks={item.links}
            productBaseImgAlt={`page ${index + 1}/${this.items.length}`}
            isMobile={!this.isDesktopScreen}
            pageIndex={index}
            mainSliderIndex={this.mainSliderIndex}
            caption={item.caption}
            user-name={item.username}
            created-time={item.created_time}
            rating={item.rating !== null ? item.rating : 0}
            page-id={item.id}
            appStore={this.appStore}
            tabindex={index === this.mainSliderIndex ? 0 : -1}
            aria-hidden={`${index !== this.mainSliderIndex}`}
          ></covet-pics-highlighted-page>)
        }
        <button disabled={this.mainSliderIndex === this.numberOfPages - 1}
          onClick={(e) => this.mainSliderChangeSlide(e, "next")}
          class={`slider-control main prev ${this.layout === "spotlight" ? "main-spotlight" : ""}`}
          aria-label="Next page"
          aria-controls={this.pageSliderId}
          type="button">
          {this.renderControlIcon("prev")}
        </button>
      </section>
    );
  }

  private renderVertical() {
    return (
      this.items.map((item, index) =>
        <covet-pics-highlighted-page class={this.layout}
          thumbnailUrl={item.data_type === "image" ? item.images.high_resolution : null} // TODO need add carousel, (temp ver)
          thumbnailAlt={this.thumbnailAltTag(item.data_type, item.alt_tag, item.caption, index, this.items.length, item.created_time)}
          ariaLabel={this.pageAriaLabel(item, index, this.items.length, this.layout, !this.isDesktopScreen)}
          thumbnailLayout={item.data_type}
          thumbnailCarouselImages={item.data_type === "carousel" ? item.carousel_media : null}
          videoUrl={item.video ? item.video : null}
          videoPoster={item.cover_images && item.cover_images.high_resolution ? item.cover_images.high_resolution : null}
          productLinks={item.links}
          productBaseImgAlt={`page ${index + 1}/${this.items.length}`}
          isMobile={!this.isDesktopScreen}
          reverseClass={index % 2 !== 0 && this.isDesktopScreen && this.layout === "zigzag" ? "reverse" : ""}
          caption={item.caption}
          user-name={item.username}
          created-time={item.created_time}
          rating={item.rating !== null ? item.rating : 0}
          item-type={item.data_type}
          page-id={item.id}
          appStore={this.appStore}
        ></covet-pics-highlighted-page>));
  }

  private async mainSliderAnimationLoop() {
    if (this.mainSliderAnimatingSlides.length !== 0) {
      try {
        this.mainSliderAnimatingSlides.length > 2
          ? await this.mainSliderAnimateSlides(this.mainSliderAnimatingSlides[0], true)
          : await this.mainSliderAnimateSlides(this.mainSliderAnimatingSlides[0], false);
        this.mainSliderAnimatingSlides.splice(0, 1);
        return this.nextTick(() => this.mainSliderAnimationLoop());
      } catch (error) {
        return this.nextTick(() => this.mainSliderAnimationLoop());
      }
    } else {
      return this.nextTick(() => this.mainSliderAnimationLoop());
    }
  }

  private renderMainLayout() {
    if (this.layout === "slider" || this.layout === "spotlight") return this.renderSlider();
    if (this.layout === "zigzag" || this.layout === "row") return this.renderVertical();
    return null;
  }

  private mainSliderChangeSlide = (event: MouseEvent, direction: string) => {
    event.preventDefault();
    event.stopPropagation();

    const maxIndex: number = this.numberOfPages < this.items.length
      ? this.numberOfPages
      : this.items.length;

    if (direction === "next" && this.mainSliderIndex < maxIndex - 1) {
      this.mainSliderIndex++;
      return this.mainSliderAnimatingSlides.push(this.mainSliderIndex);
    }
    if (direction === "prev" && this.mainSliderIndex > 0) {
      this.mainSliderIndex--;
      return this.mainSliderAnimatingSlides.push(this.mainSliderIndex);
    }

    return this.mainSliderIndex;
  }

  private async mainSliderAnimateSlides(newIndex: number, fastMode: boolean = false) {
    const slides: Array<HTMLElement> = Array.from(this.el.querySelectorAll(".main-slider .page"));
    const slideToHide: HTMLElement = this.el.querySelector(".main-slider .slide-active");
    const slideToShow: HTMLElement = slides[newIndex];

    if (slideToHide === null || slideToShow === null) return Promise.reject("notloaded");

    const hide = new Promise(resolve => {
      const leaveHandler = () => {
        slideToHide.classList.add("slide-noactive");
        slideToHide.classList.remove("leave", "slide-active", "fast");
        slideToHide.removeEventListener("transitionend", leaveHandler);
        this.nextTick(() => resolve("done"));
      };
      slideToHide.addEventListener("transitionend", leaveHandler);
      fastMode ? slideToHide.classList.add("leave", "fast") : slideToHide.classList.add("leave");
    });

    const show = new Promise(resolve => {
      const showHandler = () => {
        slideToShow.classList.add("slide-active");
        slideToShow.classList.remove("before-enter", "slide-noactive", "enter", "fast");
        slideToShow.removeEventListener("transitionend", showHandler);
        this.nextTick(() => resolve("done"));
      };

      slideToShow.classList.add("before-enter");
      this.nextTick(() => {
        slideToShow.addEventListener("transitionend", showHandler);
        fastMode ? slideToShow.classList.add("enter", "fast") : slideToShow.classList.add("enter");
      });
    });
    return Promise.all([hide, show]);
  }

  render() {
    const showPagination = ((this.layout === "slider" && !this.isDesktopScreen) || this.layout === "spotlight")
      && this.items && this.items.length > 1;
    return (
      <Host class={this.layout} style={this.setCSSVariables()} onClick={(e) => !this.isDesktopScreen && this.layout === "row" ? this.isClickOutsideHotspot(e) : null}
        role={"region"} aria-label="Page gallery" aria-roledescription={`Gallery ${this.items.length} posts`}>
        {this.renderMainLayout()}
        {showPagination &&
          <span class="page-pagination">{`${this.mainSliderIndex + 1}/${this.items.length}`}</span>
        }
      </Host>
    );
  }
}
